<template>
  <div class="batch-wrapper" @mouseenter="batchMouseEnter" @mouseleave="batchMouseLeave"
       @click="handleBatchClickHandler">
    <div class="title-wrapper">
      <span class="batch-title">Run {{ data.systemId }}</span>
      <v-progress-circular v-if="progress('getSampleBatchList')" class="ml-2" width="2" size="18" color="primary"
                           indeterminate></v-progress-circular>
      <span class="toggle" v-show="showToggle"><i class="fas fa-caret-down fa-2x"></i></span>
      <v-chip class="status" small :color="batchStatusColor(data.status)" text-color="white">{{ batchStatus(data.status) }}</v-chip>
      <div class="details"><span><b>{{ data.samplesCount }}/{{ data.size }}</b></span>,
        <span>created {{ data.createdAt | DateWithMinutes }}</span>
        <span v-if="responsibleSpecialistName">, responsible: {{ responsibleSpecialistName }}</span>
        <span v-if="data.startSeqDatetime">, sequence started: {{ data.startSeqDatetime | DateWithMinutes }}</span>
        <span v-if="data.samplesReportedDatetime">, samples reported: {{ data.samplesReportedDatetime | DateWithMinutes }}</span>
        <span v-if="data.files && data.files.length > 0">, attached files: {{data.files.length}}</span>
      </div>
    </div>
    <div class="toggle text-right"></div>
    <div class="samples-wrapper" v-if="detailsMode" @click.stop="">
      <div class="files-wrapper">
        <file-list :items="batchFiles" @delete="handleDeleteFile" @fileClick="fileViewerOpen($event)" compact-mode>
        </file-list>
        <div style="clear: both;"></div>
        <div class="report-file-upload text-right">
          <file-upload
              ref="fileUploader"
              :input-id="batchFileUploadId"
              v-model="batchFiles"
              :post-action="uploadUrl"
              multiple
              :customAction="onBatchFileUpload"
              @input-file="handleBatchFileUploaderInputFile">
            <v-btn x-small color="primary" @click="() => {}">Attach file</v-btn>
          </file-upload>
        </div>
      </div>
      <v-divider class="mt-2 mb-2"></v-divider>
      <sample-batch-container :batch="data"
                              :batchSamplesByPosition="batchSamples(data.id)"></sample-batch-container>
      <div class="mt-3">
        <v-btn v-if="sendToWorkButtonVisible"
               class="ma-2"
               :loading="progress('startBatchProcessing')"
               :disabled="isDisabledStartProcessingButton"
               color="primary"
               @click="handleInitiateStartProcessingButtonClick">Start processing
        </v-btn>
        <v-btn class="ma-2"
               :loading="progress('exportBatchToCsv')"
               :disabled="progress('exportBatchToCsv')"
               color="primary"
               @click="handleExportToCsvButtonClick">Export to CSV
        </v-btn>
        <v-btn class="ma-2"
               :loading="progress('exportBatchSamplesToXlsx')"
               :disabled="progress('exportBatchSamplesToXlsx')"
               color="primary"
               @click="handleExportToXlsxButtonClick">Export to XLSX
        </v-btn>
        <v-btn class="ma-2"
               :loading="progress('labelsPrinting')"
               :disabled="progress('labelsPrinting')"
               color="primary"
               @click="handleprintLabelsButtonClick">Print labels
        </v-btn>
      </div>
    </div>
    <v-dialog v-model="startProcessingDialogVisible" max-width="500" :persistent="true">
      <v-card>
        <v-card-title class="headline">Select responsible specialist</v-card-title>

        <v-card-text class="text--primary">
          <v-form v-model="startProcessingFormValid">
            <v-autocomplete
                v-model="responsibleSpecialistId"
                :items="availableResponsibleSpecialists"
                :rules="responsibleSpecialistRules"
                item-text="name"
                label="Please select responsible specialist"
                item-value="id"
                :menu-props="{'maxWidth':'400', 'minWidth': 400, 'nudge-left': 16}">
            </v-autocomplete>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
              color="primary darken-1"
              :disabled="!startProcessingFormValid"
              @click="handleStartProcessingButtonClick"
          >
            Start processing
          </v-btn>

          <v-btn
              color="gray darken-1"
              text
              @click="startProcessingDialogVisible = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import FileSaver from 'file-saver'
import SampleBatchContainer from './SampleBatchContainer';
import DateWithMinutes from '../Filters/DateWithMinutes'
import {mapActions, mapGetters} from 'vuex'
import FileList from '../Files/List'
import _ from 'lodash'
import {getBaseApiURL} from '@/helpers/baseurl'

export default {
  name: 'Batch',
  props: {
    data: Object,
  },
  components: {
    SampleBatchContainer,
    FileList
  },
  filters: {
    DateWithMinutes
  },
  data() {
    return {
      responsibleSpecialistId: null,
      active: false,
      detailsMode: false,
      startProcessingDialogVisible: false,
      startProcessingFormValid: false,
      batchFileUploadId: _.uniqueId('batch-file-upload-'),
      batchFiles: this.data.files || [],
      uploadUrl: `${getBaseApiURL()}files/upload`,
      responsibleSpecialistRules: [
        v => !!v || 'Please, select responsible specialist'
      ]
    }
  },
  computed: {
    ...mapGetters('batches', {
          batchStatus: 'batchStatus',
          progress: 'progress',
          batchSamples: 'batchSamples'
        }
    ),
    ...mapGetters({
      availableSpecialistsByIds: 'availableSpecialistsByIds',
      canSeqPGTSpecialists: 'canSeqPGTSpecialists',
    }),
    showToggle() {
      return this.active && !this.detailsMode
    },
    sendToWorkButtonVisible() {
      return this.data.status === 'preparing'
    },
    isDisabledStartProcessingButton() {
      return this.progress('startBatchProcessing') || this.data.samplesCount === 0
    },
    availableResponsibleSpecialists() {
      return _.orderBy(this.canSeqPGTSpecialists, ['name'], ['asc'])
    },
    responsibleSpecialistName() {
      return _.get(this.availableSpecialistsByIds, [this.data.responsibleSpecialistId, 'name'], null)
    }
  },
  methods: {
    ...mapActions({
      fileViewerOpen: 'fileViewerOpen'
    }),
    ...mapActions('batches', {
      addFileToBatch: 'addFileToBatch',
      deleteFileFromBatch: 'deleteFileFromBatch',
      getSampleBatchList: 'getSampleBatchList',
      startBatchProcessing: 'startBatchProcessing',
      printLabels: 'printLabels',
      exportBatchToCsv: 'exportBatchToCsv',
      exportBatchSamplesToXlsx: 'exportBatchSamplesToXlsx'
    }),
    batchMouseEnter() {
      this.active = true
    },
    batchMouseLeave() {
      this.active = false
    },
    handleBatchClickHandler() {
      this.detailsMode = !this.detailsMode

      if (!this.detailsMode) {
        return
      }

      this.getSampleBatchList(this.data.id)
    },
    handleInitiateStartProcessingButtonClick() {
      this.responsibleSpecialistId = null
      this.startProcessingDialogVisible = true
    },
    async handleStartProcessingButtonClick() {
      if (!this.responsibleSpecialistId) {
        return
      }

      let started = await this.startBatchProcessing({
        batchId: this.data.id,
        responsibleSpecialistId: this.responsibleSpecialistId
      })

      if (started) {
        this.responsibleSpecialistId = null
        this.startProcessingDialogVisible = false
      }
    },
    async handleExportToCsvButtonClick() {
      const fileData = await this.exportBatchToCsv(this.data.id)
      let blob = new Blob([fileData], {type: "text/csv"})
      FileSaver.saveAs(blob, `run-${this.data.systemId}-${moment().format('YYYY-MM-DD-HH-mm-ss')}.csv`)
    },
    async handleExportToXlsxButtonClick() {
      const fileInfo = await this.exportBatchSamplesToXlsx(this.data.id)
      const fileData = fileInfo.data
      const fileName = fileInfo.fileName

      const blob = new Blob([fileData], {type: ' application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
      FileSaver.saveAs(blob, fileName);
    },
    handleprintLabelsButtonClick() {
      this.printLabels(this.data.id)
    },
    async onBatchFileUpload(file, component) {
      const result = await this.onFileUpload(file, component)

      if (this.data.id && result.success) {
        this.addFileToBatch({batchId: this.data.id, fileId: result.fileId})
      }

      return result
    },
    handleBatchFileUploaderInputFile(newFile, oldFile) {
      // Automatic upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (!this.$refs.fileUploader.active) {
          this.$refs.fileUploader.active = true
        }
      }

      // Auto delete on error
      if (!newFile.active && !newFile.success && newFile.error.length > 0) {
        let fileIndex = _.findIndex(this.batchFiles, function (file) {
          return file.id === newFile.id
        })

        if (fileIndex < 0) {
          return
        }

        this.batchFiles.splice(fileIndex, 1)
      }
    },
    handleDeleteFile(fileId) {
      this.deleteFileFromBatch({batchId: this.data.id, fileId: fileId})
      let fileIndex = _.findIndex(this.batchFiles, function (file) {
        return file.fileId === fileId
      })

      if (fileIndex < 0) {
        return
      }

      this.batchFiles.splice(fileIndex, 1)
    },
    async onFileUpload(file, component) {
      let result = await component.uploadHtml5(file)
      const response = result.response

      if (response.error !== null) {
        let resultErrors = []

        if (_.isString(response.error)) {
          resultErrors.push(response.error)
        }

        if (_.isObject(response.error)) {
          for (let key in response.error) {
            if (!response.error.hasOwnProperty(key)) {
              continue
            }

            resultErrors.push(response.error[key])
          }
        }
        result.error = _.join(resultErrors, '; ')
        result.success = false

        this.$snotify.error(`Unable to upload file '${file.name}'. Please try again later`)
        return result
      }

      let fileId = _.get(response.data, 'fileId', null)

      if (fileId === null) {
        result.error = this.$t('files.errors.unableToUploadFile')
        result.success = false

        this.$snotify.error(`Unable to upload file '${file.name}'. Please try again later`)

        return result
      }

      result.fileId = fileId
      result.mimeType = _.get(response.data, 'mimeType', null)
      result.success = true

      return result
    },
    batchStatusColor(status) {
      if (status === 'done') {
        return 'success'
      }

      return 'primary'
    }
  }
}
</script>

<style scoped>
.batch-wrapper {
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #cecece;
  border-radius: 5px;
}

.batch-wrapper:hover {
  border: 1px solid #acacac;
}

.batch-title {
  font-size: 20px !important;
  font-weight: 500;
  line-height: 1 !important;
  letter-spacing: .02em !important;
  font-family: Roboto, sans-serif !important
}

.title-wrapper {
  position: relative;
  cursor: pointer;
}

.status {
  float: right;
}

.details {
  font-size: 10px;
  color: #545454;
}

.toggle {
  color: #acacac;
  opacity: 1;
  position: absolute;
  left: 50%;
  top: 45px;
}

.files-wrapper {
  display: flex;
}

</style>